import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, Plus, X, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, toMinor } from "@/lib/format";
import { EventDialog, Field, SelectField } from "@/components/catalogue/CatalogueDialogs";

const PLAN_LABEL = { full: "Full", deposit_balance: "Deposit + Balance", instalment: "Instalment" };
const PLAN_TONE = {
  full: "bg-emerald-50 text-emerald-700 border-emerald-200",
  deposit_balance: "bg-amber-50 text-amber-700 border-amber-200",
  instalment: "bg-blue-50 text-blue-700 border-blue-200",
};

export function PlanBadges({ plans }) {
  if (!plans?.length) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {plans.map((p) => <Badge key={p} variant="outline" className={`text-[10px] ${PLAN_TONE[p]}`} data-testid={`plan-badge-${p}`}>{PLAN_LABEL[p] || p}</Badge>)}
    </div>
  );
}

export default function EventPanel({ event, onClose, products, skus, offers, onChange, canEdit, canDelete }) {
  const [editOpen, setEditOpen] = useState(false);
  const [addOfferOpen, setAddOfferOpen] = useState(false);
  const [editOffer, setEditOffer] = useState(null);

  if (!event) return null;

  const remove = async (offerId) => {
    if (!window.confirm("Remove this offer from the event? (Product & package are kept.)")) return;
    try {
      await api.delete(`/catalogue/offers/${offerId}`);
      toast.success("Offer removed");
      onChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Remove failed");
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Delete event ${event.code}? This can only be done when it has no offers or linked sales.`)) return;
    try {
      await api.delete(`/catalogue/events/${event.id}`);
      toast.success("Event deleted");
      onChange?.();
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "This event is linked to existing transactions and cannot be deleted.");
    }
  };

  return (
    <Sheet open={!!event} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="event-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{event.code}</span>
            <span>{event.name}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <section className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Event details</div>
              <div className="flex gap-1">
                {canEdit && <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)} data-testid="event-panel-edit"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
                {canDelete && <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={deleteEvent} data-testid="event-panel-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Detail label="Kind"><span className="capitalize">{event.kind}</span></Detail>
              <Detail label="Packages offered">{offers.length}</Detail>
              <Detail label="Start date">{event.start_date || "—"}</Detail>
              <Detail label="End date">{event.end_date || "—"}</Detail>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Offers in this event</div>
              {canEdit && (
                <Button size="sm" onClick={() => setAddOfferOpen(true)} data-testid="add-offer-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add offer
                </Button>
              )}
            </div>
            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="w-28">Package</TableHead>
                    <TableHead className="w-32 text-right">Price</TableHead>
                    <TableHead>Allowed plans</TableHead>
                    <TableHead className="w-16 text-center">Instl.</TableHead>
                    <TableHead className="w-20 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No offers yet — click Add offer.</TableCell></TableRow>}
                  {offers.map((o) => {
                    const sku = skus.find((s) => s.id === o.sku_id);
                    const product = sku ? products.find((p) => p.id === sku.product_id) : null;
                    const plans = o.allowed_plans?.length ? o.allowed_plans : [o.default_plan || "full"];
                    const instl = o.default_instalments || o.instalments || 1;
                    return (
                      <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                        <TableCell className="text-sm">{product?.name || "—"}<div className="text-[10px] text-slate-400 font-mono">{product?.code || ""}</div></TableCell>
                        <TableCell className="font-mono text-xs">{sku?.code || "—"}<div className="text-[10px] text-slate-400">{sku?.name}</div></TableCell>
                        <TableCell className="text-right font-mono">{fmtMoney(o.price_minor, o.currency)}</TableCell>
                        <TableCell><PlanBadges plans={plans} /></TableCell>
                        <TableCell className="text-center font-mono text-xs">{plans.includes("instalment") ? instl : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditOffer(o)} data-testid={`offer-edit-${o.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                            {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-rose-50" onClick={() => remove(o.id)} data-testid={`offer-remove-${o.id}`}><X className="w-3.5 h-3.5 text-rose-600" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>

        <EventDialog open={editOpen} onOpenChange={setEditOpen} event={event} onDone={() => { setEditOpen(false); onChange?.(); toast.success("Event updated"); }} />
        <OfferDialog
          open={addOfferOpen || !!editOffer}
          onOpenChange={(v) => { if (!v) { setAddOfferOpen(false); setEditOffer(null); } }}
          event={event}
          offer={editOffer}
          products={products}
          skus={skus}
          onDone={() => { setAddOfferOpen(false); setEditOffer(null); onChange?.(); }}
        />
      </SheetContent>
    </Sheet>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function OfferDialog({ open, onOpenChange, event, offer, products, skus, onDone }) {
  const editing = !!offer;
  const [productId, setProductId] = useState("");
  const [inlineMode, setInlineMode] = useState(null); // null | "product" | "package"
  const [form, setForm] = useState({
    sku_id: "", price: "", currency: "MYR",
    allowed_plans: ["full"], default_instalments: 3, version: 1,
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setInlineMode(null);
    if (offer) {
      const sku = skus.find((s) => s.id === offer.sku_id);
      setProductId(sku?.product_id || "");
      setForm({
        sku_id: offer.sku_id,
        price: (offer.price_minor / 100).toFixed(2),
        currency: offer.currency || "MYR",
        allowed_plans: offer.allowed_plans?.length ? offer.allowed_plans : [offer.default_plan || "full"],
        default_instalments: offer.default_instalments || offer.instalments || 3,
        version: offer.version || 1,
      });
    } else {
      setProductId("");
      setForm({ sku_id: "", price: "", currency: "MYR", allowed_plans: ["full"], default_instalments: 3, version: 1 });
    }
  }, [offer, open, skus]);

  const packageOptions = skus.filter((s) => s.product_id === productId);

  const togglePlan = (plan) => {
    setForm((f) => {
      const has = f.allowed_plans.includes(plan);
      let next = has ? f.allowed_plans.filter((p) => p !== plan) : [...f.allowed_plans, plan];
      if (next.length === 0) next = [plan];
      return { ...f, allowed_plans: next };
    });
  };

  const submit = async () => {
    if (!form.sku_id) { toast.error("Select a product and package"); return; }
    if (!form.price || Number(form.price) <= 0) { toast.error("Enter a price"); return; }
    setSaving(true);
    try {
      const defaultPlan = form.allowed_plans[0];
      const payload = {
        event_id: event.id, sku_id: form.sku_id,
        price_minor: toMinor(form.price), currency: form.currency,
        tax_inclusive: true,
        default_plan: defaultPlan,
        allowed_plans: form.allowed_plans,
        default_instalments: parseInt(form.default_instalments || 1),
        instalments: parseInt(form.default_instalments || 1),
        version: parseInt(form.version || 1),
      };
      if (editing) await api.patch(`/catalogue/offers/${offer.id}`, payload);
      else await api.post("/catalogue/offers", payload);
      toast.success(editing ? "Offer updated" : "Offer added");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto z-[60]" data-testid="offer-dialog">
        <SheetHeader><SheetTitle>{editing ? "Edit offer" : "Add offer to event"}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-4">
          {/* Product picker (or inline product create) */}
          {inlineMode === "product" ? (
            <InlineProductForm
              onCancel={() => setInlineMode(null)}
              onSaved={async (created) => {
                setProductId(created.id);
                setForm((f) => ({ ...f, sku_id: "" }));
                setInlineMode(null);
                await onDone?.();
                toast.success(`Product ${created.code} created — pick a package next`);
              }}
            />
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-wider text-slate-600">Product</div>
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setInlineMode("product")} data-testid="offer-new-product">+ New product</Button>
              </div>
              <SelectField label="" v={productId} onChange={(v) => { setProductId(v); setForm({ ...form, sku_id: "" }); }}
                options={products.map((p) => [p.id, `${p.code} · ${p.name}`])} testid="offer-product" />
            </div>
          )}

          {/* Package picker (or inline package create) */}
          {inlineMode === "package" ? (
            <InlinePackageForm
              productId={productId}
              productLabel={products.find((p) => p.id === productId)?.name || ""}
              onCancel={() => setInlineMode(null)}
              onSaved={async (created) => {
                setForm((f) => ({ ...f, sku_id: created.id, price: created.default_price_minor ? (created.default_price_minor / 100).toFixed(2) : f.price }));
                setInlineMode(null);
                await onDone?.();
                toast.success(`Package ${created.code} created`);
              }}
            />
          ) : (
            inlineMode !== "product" && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wider text-slate-600">Package</div>
                  {productId && <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setInlineMode("package")} data-testid="offer-new-package">+ New package</Button>}
                </div>
                <SelectField label="" v={form.sku_id} onChange={(v) => setForm({ ...form, sku_id: v })}
                  options={packageOptions.map((s) => [s.id, `${s.code} · ${s.name}`])} testid="offer-package" />
                {productId && packageOptions.length === 0 && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No packages under this product yet — create one.</div>
                )}
              </div>
            )
          )}

          {/* The rest of the offer form is hidden while an inline create is open,
              so the panel stays focused on one action at a time. */}
          {inlineMode === null && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Field label="Price (MYR)" v={form.price} onChange={(v) => setForm({ ...form, price: v })} type="number" placeholder="0.00" testid="offer-price" />
                </div>
                <SelectField label="Currency" v={form.currency} onChange={(v) => setForm({ ...form, currency: v })}
                  options={[["MYR", "MYR"], ["SGD", "SGD"], ["USD", "USD"]]} />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-600 mb-1">Allowed payment plans</div>
                <div className="space-y-1.5">
                  {[["full", "Full payment"], ["deposit_balance", "Deposit + Balance"], ["instalment", "Instalment"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`offer-plan-${k}`}>
                      <Checkbox checked={form.allowed_plans.includes(k)} onCheckedChange={() => togglePlan(k)} />
                      <span>{l}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">First checked plan is the default. At least one must remain selected.</div>
              </div>

              {form.allowed_plans.includes("instalment") && (
                <Field label="Default instalment count" v={form.default_instalments} onChange={(v) => setForm({ ...form, default_instalments: v })} type="number" placeholder="3" testid="offer-default-instalments" />
              )}
            </>
          )}
        </div>
        {inlineMode === null && (
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="offer-submit">{saving ? "Saving…" : (editing ? "Save changes" : "Add offer")}</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const FAMILY_OPTS = [["one_time", "One-time"], ["subscription", "Subscription"], ["instalment", "Instalment"]];

function InlineProductForm({ onSaved, onCancel }) {
  const [form, setForm] = useState({ code: "", name: "", family: "one_time", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.code || !form.name) { toast.error("Product code and name are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/catalogue/products", {
        code: form.code, name: form.name, family: form.family, description: form.description || null,
      });
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };
  return (
    <section className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="inline-new-product">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-700 font-semibold">New product</div>
        <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={onCancel} data-testid="inline-new-product-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to list
        </Button>
      </div>
      <Field label="Product code" v={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="inline-product-code" />
      <Field label="Product name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="inline-product-name" />
      <SelectField label="Family" v={form.family} onChange={(v) => setForm({ ...form, family: v })} options={FAMILY_OPTS} testid="inline-product-family" />
      <Field label="Description" v={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="inline-product-description" />
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving} data-testid="inline-product-submit">{saving ? "Saving…" : "Save product"}</Button>
      </div>
    </section>
  );
}

function InlinePackageForm({ productId, productLabel, onSaved, onCancel }) {
  const [form, setForm] = useState({ code: "", name: "", description: "", price: "", duration_months: 0, recognition_policy: "on_payment_date" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.code || !form.name) { toast.error("Package code and name are required"); return; }
    const price = String(form.price ?? "").trim();
    if (price === "" || Number(price) < 0) { toast.error("Enter a default price (0 or greater)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/catalogue/skus", {
        product_id: productId, code: form.code, name: form.name,
        default_description: form.description || null,
        duration_months: parseInt(form.duration_months || 0),
        recognition_policy: form.recognition_policy,
        default_price_minor: Math.round(Number(price) * 100),
      });
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };
  return (
    <section className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="inline-new-package">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-700 font-semibold">New package{productLabel && <span className="ml-1 text-slate-500 normal-case">for {productLabel}</span>}</div>
        <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={onCancel} data-testid="inline-new-package-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to list
        </Button>
      </div>
      <Field label="Package code" v={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="inline-package-code" />
      <Field label="Package name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="inline-package-name" />
      <Field label="Description" v={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="inline-package-description" />
      <Field label="Default price (MYR)" v={form.price} onChange={(v) => setForm({ ...form, price: v })} type="number" placeholder="0.00" testid="inline-package-price" />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Duration (months)" v={form.duration_months} onChange={(v) => setForm({ ...form, duration_months: v })} type="number" />
        <SelectField label="Recognition" v={form.recognition_policy} onChange={(v) => setForm({ ...form, recognition_policy: v })}
          options={[["monthly_ratable", "Monthly ratable"], ["on_payment_date", "On payment date"], ["on_fulfillment", "On fulfilment"]]} />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving} data-testid="inline-package-submit">{saving ? "Saving…" : "Save package"}</Button>
      </div>
    </section>
  );
}
